/*
 * File: EmailAgreements.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 9:54 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, forwardRef, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { stripHtml } from '../../../../utilities/utilities';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';
import { TextInput } from '../../TextInput';
import { Checkbox } from '../../Checkbox';
import { ToolTip } from '../../ToolTip';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './EmailAgreements.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const EmailAgreements = forwardRef(({
	className,
	moveBackward,
	handleSubmitStory,
	submissionEmail,
	setSubmissionEmail,
	termsChecked,
	setTermsChecked,
	socialShareChecked,
	setSocialShareChecked,
	noContactChecked,
	setNoContactChecked,
	newsletterChecked,
	setNewsletterChecked,
	publishMainChecked,
	setPublishMainChecked,
	additionalAgreementsChecked,
	setAdditionalAgreementsChecked,
	isVisible,
	isSubmitting
}, ref) => {

	// Create state
	const [showAgreements, setShowAgreements] = useState(isVisible);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.agreements, [partner]);
	const partnerFeatures = useMemo(() => partner?.features?.stories, [partner]);

	// Get legal urls
	const termsUrl = useMemo(() => (partner?.legal?.links?.terms ? partner?.legal?.links?.terms : `https://www.ourwave.org${locale.localePath}/terms`), [partner, locale]);
	const privacyUrl = useMemo(() => (partner?.legal?.links?.privacy ? partner?.legal?.links?.privacy : `https://www.ourwave.org${locale.localePath}/privacy`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Handle next action
	const nextAction = async () => {

		// Handle submit story
		await handleSubmitStory();
	};

	// Perform actions on visibility change
	useEffect(() => {
		if (isVisible) {

			// Update show agreements
			setShowAgreements(true);

		} else {

			// Update show agreements
			setTimeout(() => {
				setShowAgreements(false);
			}, 300);

		}
	}, [isVisible]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.agreements.title">
								One final step.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					<Typography tag="p">
						{!partnerContent?.subtitle ? (
							<Trans t={t} i18nKey="pageShareStory.agreements.subtitle">
								To ensure that you always have control over your story, please input your email below. We will never share your personal information with anyone, but we will email you once your story has been reviewed and provide a unique link once published.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

				</S.ContentContainer>

				{/* Inputs */}
				<S.InputContainer>

					{/* Submission Email */}
					<TextInput
						placeholder={t('pageShareStory.agreements.placeholder')}
						name="message"
						type="text"
						value={submissionEmail || ''}
						onChange={(event) => {
							const { value } = event.target;
							setSubmissionEmail(stripHtml(value));
						}}
					/>

					{/* Agreements Container */}
					{showAgreements && (
						<S.AgreementsContainer>

							{/* Agreement - Terms of Service */}
							<S.AgreementRow>
								<Checkbox
									name="terms"
									value={termsChecked || false}
									onChange={(event) => {
										setTermsChecked(event.target.checked);
									}}
								/>
								<S.AgreementContent>
									{partner.is_core ? (
										<Typography tag="p" variation="2">
											<Trans t={t} i18nKey="pageShareStory.agreements.termsPrivacyAgreement">
												Check this box if you agree to our
												{' '}
												<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms of Service</LocaleLink>
												{' '}
												and
												{' '}
												<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
												.
											</Trans>
											{' '}
											<S.RequiredBadge>
												<Trans t={t} i18nKey="pageShareStory.agreements.required">
													Required
												</Trans>
											</S.RequiredBadge>
										</Typography>
									) : (
										<Typography tag="p" variation="2">
											{!partnerContent?.terms_privacy ? (
												<Trans t={t} i18nKey="pageShareStory.agreements.termsPrivacyAgreementPartner">
													Check this box if you agree to Our Wave&apos;s
													{' '}
													<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms of Service</LocaleLink>
													{' '}
													and
													{' '}
													<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
													.
												</Trans>
											) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.terms_privacy[locale.localeBaseId]), isValidNode, ParsingInstructions)}
											{' '}
											<S.RequiredBadge>
												<Trans t={t} i18nKey="pageShareStory.agreements.required">
													Required
												</Trans>
											</S.RequiredBadge>
										</Typography>
									)}
								</S.AgreementContent>
							</S.AgreementRow>

							{/* Agreement - Publish on Our Wave */}
							{!partner.is_core && (
								<S.AgreementRow>
									<Checkbox
										name="publishMain"
										value={publishMainChecked || false}
										onChange={(event) => {
											setPublishMainChecked(event.target.checked);
										}}
									/>
									<S.AgreementContent>
										<Typography tag="p" variation="2">
											{!partnerContent?.publish_main ? (
												<Trans t={t} i18nKey="pageShareStory.agreements.publishMainAgreement">
													To impact a broader community of survivors, we would also love to share your story on the
													{' '}
													<LocaleLink to="https://community.ourwave.org" target="_blank">Our Wave community page</LocaleLink>
													.
													If you are comfortable with this, please check this box.
												</Trans>
											) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.publish_main[locale.localeBaseId]), isValidNode, ParsingInstructions)}
										</Typography>
									</S.AgreementContent>
								</S.AgreementRow>
							)}

							{/* Agreement - Social Media & Research */}
							<S.AgreementRow>
								<Checkbox
									name="social"
									value={socialShareChecked || false}
									onChange={(event) => {
										setSocialShareChecked(event.target.checked);
									}}
								/>
								<S.AgreementContent>
									<Typography tag="p" variation="2">
										{!partnerContent?.social ? (
											<Trans t={t} i18nKey="pageShareStory.agreements.socialAgreement">
												We&apos;d love to be able to anonymously share your story for advocacy, policy reform, research, and social media. If you are comfortable with this, please check this box.
											</Trans>
										) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.social[locale.localeBaseId]), isValidNode, ParsingInstructions)}
									</Typography>
								</S.AgreementContent>
							</S.AgreementRow>

							{/* Agreement - Newsletter */}
							{partnerFeatures.show_newsletter_subscribe && (
								<S.AgreementRow>
									<Checkbox
										name="newsletter"
										value={newsletterChecked || false}
										onChange={(event) => {
											setNewsletterChecked(event.target.checked);
										}}
									/>
									<S.AgreementContent>
										<Typography tag="p" variation="2">
											{!partnerContent?.newsletter ? (
												<Trans t={t} i18nKey="pageShareStory.agreements.newsletterAgreement">
													Check this box if you would like to subscribe to the Our Wave email newsletter and be notified about updates, events, and ways to help out.
												</Trans>
											) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.newsletter[locale.localeBaseId]), isValidNode, ParsingInstructions)}
										</Typography>
									</S.AgreementContent>
								</S.AgreementRow>
							)}

							{/* Additional Agreements */}
							{partnerContent?.additional_agreements && partnerContent?.additional_agreements.length > 0 && partnerContent?.additional_agreements.map((agreement) => (
								<S.AgreementRow key={agreement.name}>
									<Checkbox
										name={agreement.name}
										value={additionalAgreementsChecked[agreement.name] || false}
										onChange={(event) => {
											setAdditionalAgreementsChecked({ ...additionalAgreementsChecked, [agreement.name]: event.target.checked });
										}}
									/>
									<S.AgreementContent>
										<Typography tag="p" variation="2">
											{ReactParser.parseWithInstructions(parseMarkdownLinks(agreement.label[locale.localeBaseId]), isValidNode, ParsingInstructions)}
										</Typography>
									</S.AgreementContent>
								</S.AgreementRow>
							))}

							{/* Agreement - No Contact */}
							<S.AgreementRow>
								<Checkbox
									name="noContact"
									value={noContactChecked || false}
									onChange={(event) => {
										setNoContactChecked(event.target.checked);
									}}
								/>
								<S.AgreementContent>
									<Typography tag="p" variation="2">
										{!partnerContent?.no_contact ? (
											<Trans t={t} i18nKey="pageShareStory.agreements.noContactAgreement">
												If you feel unsafe or do not want us to contact you under any circumstances, including to notify you when your story is posted, please check this box.
											</Trans>
										) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.no_contact[locale.localeBaseId]), isValidNode, ParsingInstructions)}
									</Typography>
								</S.AgreementContent>
							</S.AgreementRow>

						</S.AgreementsContainer>
					)}
				</S.InputContainer>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward} disabled={isSubmitting}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction} isLoading={isSubmitting} disabled={isSubmitting}>
						<Typography>
							<Trans t={t} i18nKey="pageShareStory.agreements.shareStoryButton">
								Share my story
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

				{/* Tooltip */}
				<ToolTip id="why-do-you-need-email" />

				{/* Email Instructions */}
				<Typography tag="p" variation="3" style={{ margin: '10px 0px 0px' }}>
					<Trans t={t} i18nKey="pageShareStory.agreements.emailInstructions1">
						If you don&apos;t receive an email from us after submitting your story, make sure to check your junk or spam folders. If you checked the last box above, we will make sure not to email you.
					</Trans>
				</Typography>
				<Typography tag="p" variation="3" style={{ margin: '10px 0px 0px' }}>
					<Trans t={t} i18nKey="pageShareStory.agreements.emailInstructions2">
						Please note: if you submit your student or company email, your organization may have access to emails we send.
					</Trans>
				</Typography>
				<Typography tag="p" variation="3" style={{ textDecoration: 'underline', display: 'inline-flex' }} data-tip={t('pageShareStory.agreements.emailWhyEmailTooltip')} data-for="why-do-you-need-email">
					<Trans t={t} i18nKey="pageShareStory.agreements.emailWhyEmail">
						Why do you need my email?
					</Trans>
				</Typography>

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

EmailAgreements.displayName = 'EmailAgreements';
EmailAgreements.propTypes = {
	className: PropTypes.string,
	moveBackward: PropTypes.func,
	handleSubmitStory: PropTypes.func,
	submissionEmail: PropTypes.string,
	setSubmissionEmail: PropTypes.func,
	termsChecked: PropTypes.bool,
	setTermsChecked: PropTypes.func,
	socialShareChecked: PropTypes.bool,
	setSocialShareChecked: PropTypes.func,
	noContactChecked: PropTypes.bool,
	setNoContactChecked: PropTypes.func,
	newsletterChecked: PropTypes.bool,
	setNewsletterChecked: PropTypes.func,
	publishMainChecked: PropTypes.bool,
	setPublishMainChecked: PropTypes.func,
	additionalAgreementsChecked: PropTypes.shape(),
	setAdditionalAgreementsChecked: PropTypes.func,
	isVisible: PropTypes.bool,
	isSubmitting: PropTypes.bool
};
EmailAgreements.defaultProps = {
	className: null,
	moveBackward: null,
	handleSubmitStory: null,
	submissionEmail: null,
	setSubmissionEmail: null,
	termsChecked: null,
	setTermsChecked: null,
	socialShareChecked: null,
	setSocialShareChecked: null,
	noContactChecked: null,
	setNoContactChecked: null,
	newsletterChecked: null,
	setNewsletterChecked: null,
	publishMainChecked: null,
	setPublishMainChecked: null,
	additionalAgreementsChecked: {},
	setAdditionalAgreementsChecked: null,
	isVisible: false,
	isSubmitting: false
};
